import { useAuth } from "@hooks/useAuth";
import { CaretDownIcon, ListIcon, SignOutIcon, XIcon } from "@phosphor-icons/react";
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import logo from "../../assets/images/logo.svg";
import "./NavBar.scss";
import { NAV_SECTIONS, isItemActive, userInitials } from "./navigation";

const NavBar = () => {
  const { authUser, signOut } = useAuth();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const userMenuRef = useRef(null);

  const closeMenu = () => setMenuOpen(false);

  // Close the user popover on outside click or Escape.
  useEffect(() => {
    if (!userOpen) return undefined;
    const onPointerDown = (e) => {
      if (!userMenuRef.current?.contains(e.target)) setUserOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setUserOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [userOpen]);

  const fullName = [authUser?.firstName, authUser?.lastName].filter(Boolean).join(" ") || authUser?.email;

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <NavLink
          to="/"
          className="navbar-logo-link"
          onClick={closeMenu}
        >
          <img
            src={logo}
            alt="Blikst"
            className="navbar-logo"
          />
        </NavLink>

        <div className="navbar-right">
          {/* User menu */}
          <div
            className="navbar-user"
            ref={userMenuRef}
          >
            <button
              type="button"
              className={`navbar-user-button${userOpen ? " open" : ""}`}
              onClick={() => setUserOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={userOpen}
            >
              <span className="navbar-user-avatar">{userInitials(authUser)}</span>
              <span className="navbar-user-text">
                <span className="navbar-user-name">{fullName}</span>
                <span className="navbar-user-subtitle">{authUser?.email}</span>
              </span>
              <CaretDownIcon
                size={14}
                className="navbar-user-caret"
              />
            </button>

            {userOpen && (
              <div
                className="navbar-user-popover"
                role="menu"
              >
                <div className="navbar-user-popover-header">
                  <span className="navbar-user-avatar large">{userInitials(authUser)}</span>
                  <div className="navbar-user-popover-identity">
                    <div className="navbar-user-popover-name">{fullName}</div>
                    <div className="navbar-user-popover-email">{authUser?.email}</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="navbar-user-signout"
                  role="menuitem"
                  onClick={signOut}
                >
                  <SignOutIcon size={15} />
                  Sign out
                </button>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="navbar-hamburger"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? <XIcon size={20} /> : <ListIcon size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="navbar-drawer">
          {NAV_SECTIONS.map((section) => (
            <div
              key={section.label}
              className="navbar-drawer-section"
            >
              <div className="navbar-drawer-section-label">{section.label}</div>
              {section.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end
                  className={() => `navbar-drawer-link${isItemActive(pathname, to) ? " active" : ""}`}
                  onClick={closeMenu}
                >
                  <Icon size={16} />
                  {label}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      )}
    </nav>
  );
};

export default NavBar;
